import { Goal, User, Post } from '../types';
import { format, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import { arSA } from 'date-fns/locale';

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
  metadata?: Record<string, any>;
}

interface ProgressData {
  date: string;
  progress: number;
}

interface CategoryData {
  category: string;
  count: number;
  completionRate: number;
}

interface UserStats {
  totalGoals: number;
  completedGoals: number;
  inProgressGoals: number;
  averageProgress: number;
  streakDays: number;
  topCategories: CategoryData[];
}

class Analytics {
  private static instance: Analytics;
  private initialized: boolean = false;
  private events: AnalyticsEvent[] = [];

  private constructor() {}

  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;
    
    // Initialize error tracking
    window.addEventListener('error', (event) => {
      this.trackError(event.error);
    });

    // Initialize performance tracking
    if ('performance' in window) {
      this.trackPerformance();
    }

    // Track user interactions
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'A') {
        this.trackEvent({
          category: 'Interaction',
          action: 'click',
          label: target.textContent || target.tagName,
          metadata: {
            path: target.getAttribute('href'),
            id: target.id,
            class: target.className,
          },
        });
      }
    });
  }

  private trackPerformance() {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        this.trackEvent({
          category: 'Performance',
          action: entry.entryType,
          label: entry.name,
          value: Math.round(entry.duration),
          metadata: {
            startTime: entry.startTime,
            duration: entry.duration,
            type: entry.entryType,
          },
        });
      }
    });

    observer.observe({ 
      entryTypes: [
        'navigation',
        'resource',
        'paint',
        'largest-contentful-paint',
        'first-input',
        'layout-shift',
      ],
    });
  }

  trackEvent(event: AnalyticsEvent) {
    if (!this.initialized) this.initialize();
    
    const enrichedEvent = {
      ...event,
      metadata: {
        timestamp: new Date(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...event.metadata,
      },
    };

    this.events.push(enrichedEvent);
    
    // In production, this would send to an analytics service
    if (process.env.NODE_ENV === 'development') {
      console.log('Analytics Event:', enrichedEvent);
    }
  }

  trackPageView(path: string, title?: string) {
    this.trackEvent({
      category: 'Navigation',
      action: 'pageview',
      label: path,
      metadata: {
        title,
        referrer: document.referrer,
        timestamp: new Date(),
      },
    });
  }

  trackError(error: Error) {
    this.trackEvent({
      category: 'Error',
      action: 'exception',
      label: error.message,
      metadata: {
        stack: error.stack,
        name: error.name,
        timestamp: new Date(),
      },
    });
  }

  async getUserStats(userId: string, goals: Goal[]): Promise<UserStats> {
    const userGoals = goals.filter(goal => goal.userId === userId);
    const completedGoals = userGoals.filter(goal => goal.progress === 100);
    const inProgressGoals = userGoals.filter(goal => goal.progress > 0 && goal.progress < 100);

    // Calculate category statistics
    const categories = userGoals.reduce((acc, goal) => {
      if (!acc[goal.category]) {
        acc[goal.category] = {
          count: 0,
          completed: 0,
        };
      }
      acc[goal.category].count++;
      if (goal.progress === 100) {
        acc[goal.category].completed++;
      }
      return acc;
    }, {} as Record<string, { count: number; completed: number; }>);

    const topCategories = Object.entries(categories)
      .map(([category, data]) => ({
        category,
        count: data.count,
        completionRate: (data.completed / data.count) * 100,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const stats = {
      totalGoals: userGoals.length,
      completedGoals: completedGoals.length,
      inProgressGoals: inProgressGoals.length,
      averageProgress: Math.round(
        userGoals.reduce((acc, goal) => acc + goal.progress, 0) / userGoals.length || 0
      ),
      streakDays: this.calculateStreak(userId, goals),
      topCategories,
    };

    // Track stats calculation
    this.trackEvent({
      category: 'Stats',
      action: 'calculate',
      label: 'user_stats',
      metadata: stats,
    });

    return stats;
  }

  private calculateStreak(userId: string, goals: Goal[]): number {
    const userGoals = goals.filter(goal => goal.userId === userId);
    const today = new Date();
    let streak = 0;
    let currentDate = today;

    while (true) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const hasProgress = userGoals.some(goal => {
        const lastUpdate = new Date(goal.updatedAt);
        return format(lastUpdate, 'yyyy-MM-dd') === dateStr && goal.progress > 0;
      });

      if (!hasProgress) break;
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    }

    return streak;
  }

  async getWeeklyProgress(userId: string, goals: Goal[]): Promise<ProgressData[]> {
    const today = new Date();
    const weekStart = startOfWeek(today);
    const weekEnd = endOfWeek(today);
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    const progress = days.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayGoals = goals.filter(goal => 
        goal.userId === userId &&
        format(new Date(goal.updatedAt), 'yyyy-MM-dd') === dateStr
      );

      const progress = dayGoals.length > 0
        ? dayGoals.reduce((acc, goal) => acc + goal.progress, 0) / dayGoals.length
        : 0;

      return {
        date: format(date, 'EEEE', { locale: arSA }),
        progress: Math.round(progress),
      };
    });

    // Track progress calculation
    this.trackEvent({
      category: 'Progress',
      action: 'calculate',
      label: 'weekly_progress',
      metadata: { progress },
    });

    return progress;
  }

  exportEvents(): AnalyticsEvent[] {
    return this.events;
  }

  clearEvents() {
    this.events = [];
  }
}

export const analytics = Analytics.getInstance();